/**
 * A guard against a real browser quirk, confirmed directly (see the driver session that found
 * it): a plain forward hash push — `location.hash = x`, no traversal involved — fires a `popstate`
 * in addition to the `hashchange` it is meant to fire alone, on both headless Chrome and (by
 * report) the Android WebView this ships in.
 *
 * Left unguarded, `back-navigation.ts` reads that spurious pop as a real back press: leaving a
 * root screen for any subpage — a teacher opening any class screen, a child opening any game —
 * calls `finishApp()` for a screen the visitor just opened, not left, and arms an exit trap
 * stamped with the new subpage's hash instead of the root's. The next REAL back press then has to
 * spend itself unwinding that bogus trap before it can do anything a visitor asked for — which is
 * the back key needing two presses where one should do.
 *
 * `history.pushState`/`replaceState` were checked too and do not produce the spurious pop — only
 * a plain hash assignment does, which is what `navigate()` uses.
 */

let pendingOwnNavigations = 0;

/** Call immediately before a forward hash push. */
export function markOwnNavigation(): void {
  pendingOwnNavigations += 1;
  // A safety net, not the primary signal: the spurious pop this guards against fires in direct
  // response to the hash assignment, well before anything else gets a turn, so it is always
  // consumed below before this runs. A REAL back press is a separate user gesture and cannot land
  // inside the same task, so clearing here on the rare chance the spurious pop never arrives
  // cannot mask one.
  setTimeout(() => {
    pendingOwnNavigations = Math.max(0, pendingOwnNavigations - 1);
  }, 0);
}

/** Whether this `popstate` is the spurious echo of our own push, consuming it if so. */
export function consumeIfOwnNavigation(): boolean {
  if (pendingOwnNavigations <= 0) return false;
  pendingOwnNavigations -= 1;
  return true;
}
